import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { CheckCircle2, Circle } from 'lucide-react';
import type { CutoverItemDto, ParallelOrderRow, ParallelStockRow } from '@thebb/shared';
import { get, put } from '../api';
import { useAuth } from '../auth';
import { Badge, ErrorBox, PageTitle, Spinner, fmtTime } from '../components/ui';

type Tab = 'checklist' | 'orders' | 'stock';

/** Milestone 7: parallel run next to Navision, Consignor and YouWe print, and the cut-over checklist. */
export function CutoverPage() {
  const [tab, setTab] = useState<Tab>('checklist');
  const [error, setError] = useState<unknown>(null);
  return (
    <div className="space-y-4">
      <PageTitle title="Skift" subtitle="Parallelkørsel og tjekliste for at slukke Navision, Consignor og YouWe print" />
      <div className="flex gap-1 border-b border-line">
        {([['checklist', 'Tjekliste'], ['orders', 'Ordrer mod Navision'], ['stock', 'Lager mod Navision']] as const).map(([k, l]) => <button key={k} className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium ${tab === k ? 'border-primary text-primary' : 'border-transparent text-muted hover:text-ink'}`} onClick={() => { setTab(k); setError(null); }}>{l}</button>)}
      </div>
      <ErrorBox error={error} />
      {tab === 'checklist' && <Checklist onError={setError} />}
      {tab === 'orders' && <OrderCompare />}
      {tab === 'stock' && <StockCompare />}
    </div>
  );
}

function Checklist({ onError }: { onError: (e: unknown) => void }) {
  const qc = useQueryClient();
  const { can } = useAuth();
  const list = useQuery({ queryKey: ['cutover'], queryFn: () => get<CutoverItemDto[]>('/cutover/checklist'), refetchInterval: 60_000 });
  const set = useMutation({ mutationFn: (x: { key: string; done: boolean; note?: string | null }) => put<CutoverItemDto[]>(`/cutover/checklist/${x.key}`, { done: x.done, note: x.note }), onSuccess: (r) => qc.setQueryData(['cutover'], r), onError });
  const items = list.data ?? [];
  const groups = [...new Set(items.map((i) => i.group))];
  const done = items.filter((i) => i.done).length;
  if (list.isLoading) return <Spinner />;
  return (
    <div className="space-y-4">
      <div className="card flex items-center gap-4 text-sm">
        <div className="text-3xl font-semibold tabular-nums">{done}/{items.length}</div>
        <div className="h-2 flex-1 overflow-hidden rounded-full bg-canvas"><div className="h-full bg-success" style={{ width: `${items.length ? (done / items.length) * 100 : 0}%` }} /></div>
        <div className="text-muted">Grønne punkter er klar. Automatiske punkter tjekkes af systemet; manuelle krydses af, når det er gjort.</div>
      </div>
      {groups.map((g) => (
        <section key={g} className="card space-y-1 p-0">
          <h2 className="m-0 px-4 pt-4 text-sm font-semibold uppercase tracking-wide text-muted">{g}</h2>
          <ul className="divide-y divide-line">
            {items.filter((i) => i.group === g).map((i) => (
              <li key={i.key} className="flex items-start gap-3 px-4 py-3 text-sm">
                {i.kind === 'manual' && can('settings.manage') ? (
                  <button className="mt-0.5" onClick={() => set.mutate({ key: i.key, done: !i.done, note: i.note })} aria-label={i.done ? 'Markér som ikke gjort' : 'Markér som gjort'}>{i.done ? <CheckCircle2 className="text-success" size={20} /> : <Circle className="text-muted" size={20} />}</button>
                ) : (
                  <span className="mt-0.5">{i.done ? <CheckCircle2 className="text-success" size={20} /> : <Circle className="text-muted" size={20} />}</span>
                )}
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2"><span className="font-medium">{i.title}</span><Badge tone={i.kind === 'auto' ? 'blue' : 'zinc'}>{i.kind === 'auto' ? 'automatisk' : 'manuelt'}</Badge>{i.link && <Link to={i.link} className="text-xs text-primary hover:underline">åbn</Link>}</div>
                  <div className="text-muted">{i.help}</div>
                  {i.detail && <div className={`text-xs ${i.done ? 'text-muted' : 'text-red-700'}`}>{i.detail}</div>}
                  {i.doneBy && <div className="text-xs text-muted">Krydset af {i.doneBy} {fmtTime(i.doneAt)}</div>}
                  {can('settings.manage') && <input className="input mt-1 h-8 text-xs" placeholder="Note" defaultValue={i.note ?? ''} onBlur={(e) => e.target.value !== (i.note ?? '') && set.mutate({ key: i.key, done: i.done, note: e.target.value || null })} />}
                </div>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

function OrderCompare() {
  const week = new Date(Date.now() - 7 * 86_400_000).toISOString().slice(0, 10);
  const [q, setQ] = useState({ from: week, to: new Date().toISOString().slice(0, 10), onlyDiffs: true });
  const params = useMemo(() => new URLSearchParams({ from: q.from, to: q.to, onlyDiffs: String(q.onlyDiffs) }).toString(), [q]);
  const list = useQuery({ queryKey: ['parallel-orders', params], queryFn: () => get<ParallelOrderRow[]>(`/cutover/orders?${params}`) });
  const kr = (m: number | null) => (m == null ? '' : (m / 100).toLocaleString('da-DK', { minimumFractionDigits: 2 }));
  const rows = list.data ?? [];
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-2 text-sm">
        <div><label className="label">Bestilt fra</label><input className="input" type="date" value={q.from} onChange={(e) => setQ({ ...q, from: e.target.value })} /></div>
        <div><label className="label">til</label><input className="input" type="date" value={q.to} onChange={(e) => setQ({ ...q, to: e.target.value })} /></div>
        <label className="flex items-center gap-2"><input type="checkbox" checked={q.onlyDiffs} onChange={(e) => setQ({ ...q, onlyDiffs: e.target.checked })} /> Kun afvigelser</label>
        <span className="text-muted">{rows.length} ordrer</span>
      </div>
      <div className="text-sm text-muted">Navisions tal kommer fra den timevise spejling af salgsordre, leverance og faktura pr. 3x-ordrenummer.</div>
      {list.isLoading ? <Spinner /> : (
        <table className="table card p-0">
          <thead><tr><th>Ordre</th><th>Shop</th><th>Her</th><th className="text-right">Sendt her</th><th>Tracking her</th><th className="text-right">Faktura her</th><th>Navision</th><th className="text-right">Sendt NAV</th><th>Tracking NAV</th><th className="text-right">Faktura NAV</th><th>Afvigelser</th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.orderId} className={r.issues.length ? 'bg-amber-50' : ''}>
                <td><Link to="/orders/$id" params={{ id: r.orderId }} className="text-primary hover:underline">{r.orderNumber}</Link></td>
                <td className="text-xs">{r.shopName}</td>
                <td>{r.ourStatus}</td>
                <td className="text-right tabular-nums">{r.ourShipped}/{r.ourOrdered}</td>
                <td className="text-xs">{r.ourTracking}</td>
                <td className="text-right tabular-nums">{kr(r.ourInvoiceMinor)}</td>
                <td>{r.navState ?? <span className="text-muted">-</span>}</td>
                <td className="text-right tabular-nums">{r.navShipped ?? ''}</td>
                <td className="text-xs">{r.navTracking}</td>
                <td className="text-right tabular-nums">{r.navInvoiceNo ? `${r.navInvoiceNo} · ${kr(r.navInvoiceMinor)}` : ''}</td>
                <td className="text-xs text-red-700">{r.issues.join('. ')}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={11} className="text-muted">{q.onlyDiffs ? 'Ingen afvigelser i perioden.' : 'Ingen ordrer i perioden.'}</td></tr>}
          </tbody>
        </table>
      )}
    </div>
  );
}

function StockCompare() {
  const list = useQuery({ queryKey: ['parallel-stock'], queryFn: () => get<ParallelStockRow[]>('/cutover/stock') });
  const [q, setQ] = useState('');
  const rows = (list.data ?? []).filter((r) => !q || r.sku.toLowerCase().includes(q.toLowerCase()) || r.title.toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2 text-sm"><input className="input w-60" placeholder="Søg SKU eller vare" value={q} onChange={(e) => setQ(e.target.value)} /><span className="text-muted">{rows.length} varer med forskel, største først (højst 1000)</span></div>
      <div className="text-sm text-muted">Vores beholdning pr. lager mod Navisions placeringsbeholdning for samme lokation, fra seneste Navision-synkronisering. Ret med en optælling.</div>
      {list.isLoading ? <Spinner /> : (
        <table className="table card p-0">
          <thead><tr><th>Lager</th><th>SKU</th><th>Vare</th><th className="text-right">Her</th><th className="text-right">Navision</th><th className="text-right">Forskel</th></tr></thead>
          <tbody>
            {rows.map((r) => <tr key={`${r.warehouseCode}-${r.sku}`}><td>{r.warehouseCode}</td><td>{r.sku}</td><td>{r.title}</td><td className="text-right tabular-nums">{r.ours}</td><td className="text-right tabular-nums">{r.navision}</td><td className={`text-right font-semibold tabular-nums ${r.difference < 0 ? 'text-red-700' : 'text-amber-700'}`}>{r.difference > 0 ? '+' : ''}{r.difference}</td></tr>)}
            {rows.length === 0 && <tr><td colSpan={6} className="text-muted">Ingen forskelle.</td></tr>}
          </tbody>
        </table>
      )}
    </div>
  );
}
